import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Star, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { CLINIC, WHATSAPP_URL } from "@/data/clinic";
import { useSiteContent } from "@/lib/siteContent";
import { useI18n } from "@/lib/i18n";
import { fireWhatsApp, firePhone, fireDirections, fireReviews, trackConversion } from "@/lib/conversions";
import SEOHead from "@/components/SEOHead";
import { dentistJsonLd, breadcrumbJsonLd } from "@/lib/schema";

export default function Contact() {
  const c = useSiteContent("contact_hero");
  const { t, pick } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(() => ({
    a: Math.floor(Math.random() * 9) + 1,
    b: Math.floor(Math.random() * 9) + 1,
  }));
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const captchaOk = useMemo(
    () => parseInt(captchaAnswer, 10) === captcha.a + captcha.b,
    [captchaAnswer, captcha]
  );

  const regenerateCaptcha = () => {
    setCaptcha({
      a: Math.floor(Math.random() * 9) + 1,
      b: Math.floor(Math.random() * 9) + 1,
    });
    setCaptchaAnswer("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error(t("contact.consentRequired"));
      return;
    }
    if (!captchaOk) {
      toast.error(t("contact.captchaError"));
      return;
    }
    setLoading(true);
    try {
      const { consent: _c, ...payload } = form;
      await api.post("/contact", payload);
      trackConversion("contact_form_submit", { source: "contact_page" });
      toast.success(t("contact.sentOk"));
      setForm({ name: "", email: "", phone: "", message: "", consent: false });
      regenerateCaptcha();
    } catch (err) {
      toast.error(t("contact.sentErr"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-page" className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
      <SEOHead
        title="Contacto · Katia Falón Odontología · Armenia, Quindío"
        description="Consultorio dental en Cra. 14 #17N-32, Armenia, Quindío. Llámanos, escríbenos por WhatsApp o encuéntranos en Google Maps."
        path="/contacto"
        jsonLd={[dentistJsonLd(), breadcrumbJsonLd([{name:"Inicio",path:"/"},{name:"Contacto",path:"/contacto"}])]}
      />
      <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">{pick(c.eyebrow, "contact.eyebrow")}</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">{pick(c.title, "contact.title")}</h1>
          <p className="mt-6 text-base leading-relaxed text-[hsl(var(--clinic-muted))]">
            {pick(c.body, "contact.body")}
          </p>
          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-start gap-3"><MapPin size={18} />{CLINIC.address}</li>
            <li className="flex items-start gap-3"><Phone size={18} /><a href={`tel:${CLINIC.phone}`} onClick={() => firePhone("contact")} data-testid="contact-phone-link">{CLINIC.phoneDisplay}</a></li>
            <li className="flex items-start gap-3"><Mail size={18} /><a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a></li>
          </ul>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => fireWhatsApp(WHATSAPP_URL, "contact")}
            data-testid="contact-whatsapp-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white"
          >
            {pick(c.whatsapp_label, "contact.whatsappOpen")}
          </a>
        </div>
        <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-[hsl(var(--clinic-border))] bg-white p-8">
          <div className="grid gap-4">
            <Field label={t("contact.fullName")} required id="c-name" testId="contact-name-input">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("contact.email")} required id="c-email" testId="contact-email-input">
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
              </Field>
              <Field label={t("contact.phone")} id="c-phone" testId="contact-phone-input">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
              </Field>
            </div>
            <Field label={t("contact.message")} required id="c-msg" testId="contact-message-input">
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input resize-none" />
            </Field>
            <label className="flex items-start gap-3 text-xs text-[hsl(var(--clinic-muted))]">
              <input type="checkbox" required checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="contact-consent" className="mt-1 accent-[hsl(var(--clinic-secondary))]" />
              <span>{t("contact.consentBefore")}<a href="/legal" className="underline">{t("contact.consentLink")}</a>{t("contact.consentAfter")}</span>
            </label>
            <div data-testid="contact-captcha" className="rounded-2xl border border-[hsl(var(--clinic-border))] bg-[hsl(var(--clinic-primary))]/5 p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">
                  {t("contact.captchaLabel")} <span className="text-[hsl(var(--clinic-secondary))]">*</span>
                </span>
                <button
                  type="button"
                  onClick={regenerateCaptcha}
                  data-testid="contact-captcha-refresh"
                  className="text-xs font-medium text-[hsl(var(--clinic-primary))] underline underline-offset-2 hover:opacity-80"
                >
                  {t("contact.captchaRefresh")}
                </button>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <span data-testid="contact-captcha-question" className="font-display text-xl font-semibold text-[hsl(var(--clinic-text))]">
                  {captcha.a} + {captcha.b} =
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value.replace(/[^0-9]/g, ""))}
                  data-testid="contact-captcha-input"
                  aria-label={t("contact.captchaAria").replace("{a}", captcha.a).replace("{b}", captcha.b)}
                  className="input max-w-[90px] text-center font-semibold"
                  placeholder="?"
                />
                {captchaAnswer !== "" && (
                  <span
                    data-testid="contact-captcha-status"
                    className={`text-xs font-medium ${captchaOk ? "text-green-600" : "text-red-500"}`}
                  >
                    {captchaOk ? t("contact.captchaCorrect") : t("contact.captchaIncorrect")}
                  </span>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || !captchaOk}
              data-testid="contact-submit-btn"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-[hsl(var(--clinic-text))] px-6 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? t("contact.sending") : t("contact.submit")}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input{width:100%;border:1px solid hsl(var(--clinic-border));border-radius:0.75rem;padding:0.75rem 1rem;font-size:0.9rem;background:white}.input:focus{outline:none;border-color:hsl(var(--clinic-primary));box-shadow:0 0 0 3px hsl(var(--clinic-primary)/0.15)}`}</style>

      {/* Google Maps + local SEO block */}
      <div className="mt-16 grid gap-8 lg:grid-cols-[1.4fr_1fr]" data-testid="contact-map-block">
        <div className="overflow-hidden rounded-3xl border border-[hsl(var(--clinic-border))] shadow-sm">
          <iframe
            title="Ubicación de Katia Falón Odontología en Armenia, Quindío"
            src={`https://maps.google.com/maps?q=${CLINIC.mapsQuery}&hl=es&z=17&output=embed`}
            width="100%"
            height="420"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map-iframe"
          />
        </div>
        <aside className="flex flex-col justify-between rounded-3xl border border-[hsl(var(--clinic-border))] bg-white p-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">{t("contact.howToArrive")}</div>
            <h2 className="mt-2 font-display text-3xl">{CLINIC.name}</h2>
            <p className="mt-3 flex items-start gap-2 text-sm text-[hsl(var(--clinic-muted))]">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              {CLINIC.address}
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="contact-reviews">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={`review-star-${i}`} size={16} className="fill-amber-500 text-amber-500" />
                ))}
              </div>
              <span className="text-sm font-semibold">4,9</span>
              <span className="text-sm text-[hsl(var(--clinic-muted))]">· 66 {t("contact.googleReviews")}</span>
            </div>
            <p className="mt-2 text-xs italic text-[hsl(var(--clinic-muted))]">{t("contact.reviewsNote")}</p>
          </div>
          <div className="mt-8 flex flex-col gap-2">
            <a
              href={CLINIC.mapsDirectionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireDirections("contact")}
              data-testid="contact-directions-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-5 py-3 text-sm font-semibold text-white"
            >
              {t("contact.directionsBtn")} <ExternalLink size={14} />
            </a>
            <a
              href={CLINIC.googleReviewsUrl || `https://www.google.com/search?q=${encodeURIComponent(CLINIC.name + " Armenia Quindío")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireReviews("contact")}
              data-testid="contact-google-search-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[hsl(var(--clinic-border))] px-5 py-3 text-sm font-semibold"
            >
              {t("contact.reviewsBtn")} <ExternalLink size={14} />
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}

function Field({ label, children, required, id, testId }) {
  return (
    <label className="block" htmlFor={id}>
      <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">
        {label} {required && <span className="text-[hsl(var(--clinic-secondary))]">*</span>}
      </span>
      <div className="mt-1" data-testid={testId}>{children}</div>
    </label>
  );
}
